import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {Column} from 'react-table';
import {Helmet} from 'react-helmet';
import {Link} from 'react-router-dom';

import {
  MinMaxFilter,
  TextFilter,
  ReactTable,
} from '../../../core/components/react-table';
import {DatasetWithUser, fetchAllDatasetsAdmin} from '../../../models/dataset';
import {fetchAllUsersAdmin} from '../../../models/user';
import {find, map} from 'lodash';
import {ApplicationShell} from '../../../core/layout/application-shell';
import {useAxios} from 'src/utils/http';

export const AdminDatasetsPage = () => {
  const api = useAxios();

  const {data: datasets, isLoading: datasetsIsLoading} = useQuery(
    ['adminDatasets'],
    () => {
      return fetchAllDatasetsAdmin(api);
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const {data: users, isLoading: usersIsLoading} = useQuery(
    ['adminUsers'],
    () => {
      return fetchAllUsersAdmin(api);
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const columns = useMemo<Array<Column<DatasetWithUser>>>(
    () => [
      {
        Header: 'Study Info',
        columns: [
          {
            Header: 'Dataset Id',
            accessor: 'id',
            Filter: TextFilter,
            filter: 'inclues',
            Cell: ({value}) => (
              <Link
                className="text-blue-600 underline hover:text-blue-800"
                to={`/admin/dataset/dicom-request-status/${value}`}
              >
                DICOM Status for {value}
              </Link>
            ),
          },
          {
            Header: 'Name',
            accessor: 'name',
            Filter: TextFilter,
            filter: 'inclues',
            Cell: ({value}) => <span title={value}>{value}</span>,
          },
          {
            Header: 'Status',
            accessor: 'status',
            Filter: TextFilter,
            filter: 'inclues',
          },
          {
            Header: 'Study Count',
            accessor: 'studyCount',
            Filter: MinMaxFilter,
            filter: 'between',
          },
          {
            Header: 'Include Undetermined',
            accessor: dataset =>
              dataset.includeUndetermined ? 'True' : 'False',
            Filter: TextFilter,
            filter: 'inclues',
          },
        ],
      },
      {
        Header: 'User Info',
        columns: [
          {
            Header: 'User ID',
            accessor: 'user.userId',
            Filter: TextFilter,
            filter: 'exactText',
          },
          {
            Header: 'Email',
            accessor: 'user.email',
            Filter: TextFilter,
            filter: 'inclues',
            Cell: ({value}: {value: string}) => (
              <span title={value}>{value}</span>
            ),
          },
          {
            Header: 'First Name',
            accessor: 'user.firstname',
            Filter: TextFilter,
            filter: 'inclues',
          },
          {
            Header: 'Last Name',
            accessor: 'user.lastname',
            Filter: TextFilter,
            filter: 'inclues',
          },
        ],
      },
    ],
    []
  );

  const data = useMemo(
    () =>
      datasets && users
        ? map(datasets, dataset => {
            const datasetWithUser: DatasetWithUser = {
              ...dataset,
              user: find(users, {userId: dataset.userId})!,
            };

            return datasetWithUser;
          })
        : [],
    [datasets, users]
  );

  return (
    <>
      <Helmet>
        <title>Segmed Openda - Admin - Manage Datasets</title>
      </Helmet>

      <ApplicationShell contained={false}>
        <h1 className="text-2xl mb-5">All Datasets</h1>

        {datasetsIsLoading || usersIsLoading ? (
          'Loading datasets and users...'
        ) : (
          <ReactTable
            tableColumns={columns}
            tableData={data}
            showCountDesc={'Datasets'}
            defaultSortBy={[{id: 'id', desc: true}]}
          />
        )}
      </ApplicationShell>
    </>
  );
};
